<template>
  <div class="app-shell">
    <LoaderAnimation />
    <NavbarComponent v-if="!shouldHideNavbar" />
    <BlogNavigation v-if="showBlogNavigation" class="mt-5"/>
    <main class="app-content">
      <router-view />
    </main>
    <RoulleteOffers v-if="!shouldHideNavbar" />
    <MobileBottomNav v-if="!shouldHideNavbar" />
    <FooterComponent v-if="!shouldHideNavbar" />
    <!-- WhatsApp Floating Button -->
    <a :href="whatsappUrl" 
      class="whatsapp-float text-decoration-none" 
      target="_blank" 
      rel="noopener noreferrer"
      v-if="!shouldHideNavbar && !isFooterVisible">
      <span class="whatsapp-tooltip">¿Necesitas ayuda?</span>
      <i class="fab fa-whatsapp whatsapp-icon"></i>
    </a>
  </div>
</template>
<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import NavbarComponent from '@/components/NavbarComponent.vue'
import FooterComponent from '@/components/FooterComponent.vue'
import LoaderAnimation from '@/components/Loader.vue'
import BlogNavigation from '@/components/BlogNavigation.vue'
import MobileBottomNav from '@/components/MobileBottomNav.vue'
import RoulleteOffers from '@/components/RoulleteOffers.vue'
import { useMaintenanceMonitor } from '@/utils/maintenanceMonitor'
import brand from '@/config/brand'
import { loadSiteContactPublic, siteContactPublicRef } from '@/composables/useSiteContact'

const route = useRoute()
const isAdmin = ref(localStorage.getItem('isAdmin') === 'true')
const isFooterVisible = ref(false)

let handleScrollOrResize = null

onMounted(() => {
  loadSiteContactPublic()
})

const whatsappUrl = computed(() => {
  const c = siteContactPublicRef.value
  const raw =
    c && typeof c === 'object'
      ? (c.contact_whatsapp || c.contact_phone || c.phone || '')
      : ''
  const digits = String(raw || '').replace(/\D/g, '')
  const num =
    digits || String(brand.contactWhatsapp || brand.contactPhone || '').replace(/\D/g, '')
  if (!num) return ''
  const msg = (c && typeof c === 'object' && c.whatsapp_message) || brand.whatsappMessage
  return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`
})

// Check if current route is an admin route or account page when user is admin
const shouldHideNavbar = computed(() => {
  // Ocultar navbar/footer en rutas de blog para todos
  if (route.path.startsWith('/blog')) {
    return true
  }
  // Ocultar navbar/footer en cualquier ruta de /configuracion (panel usuario o admin)
  if (route.path.startsWith('/configuracion')) {
    return true
  }
  if (route.path.startsWith('/mantenimiento')) {
    return true
  }
  return false
})

// Show blog navigation on blog routes
const showBlogNavigation = computed(() => {
  return route.path.startsWith('/blog')
})

// Update isAdmin value when localStorage changes
const updateAdminStatus = () => {
  isAdmin.value = localStorage.getItem('isAdmin') === 'true'
}

const updateFooterVisibility = () => {
  const footerEl = document.querySelector('footer')

  if (!footerEl) {
    isFooterVisible.value = false
    return
  }

  const rect = footerEl.getBoundingClientRect()
  isFooterVisible.value = rect.top < window.innerHeight && rect.bottom > 0
}

// Watch for route changes to recheck conditions
watch(() => route.path, () => {
  updateAdminStatus()
  updateFooterVisibility()
})

onMounted(() => {
  // Initial check
  updateAdminStatus()
  updateFooterVisibility()
  
  // Inicializar verificación de mantenimiento con TTL más largo (5 minutos)
  // para reducir consultas al servidor
  useMaintenanceMonitor(5) // Check every 5 minutes (aumentado de 2 a 5 minutos)
  
  // Listen for storage events (in case user logs in/out in another tab)
  window.addEventListener('storage', () => {
    updateAdminStatus()
  })

  handleScrollOrResize = () => updateFooterVisibility()
  window.addEventListener('scroll', handleScrollOrResize, { passive: true })
  window.addEventListener('resize', handleScrollOrResize)
})

onBeforeUnmount(() => {
  if (handleScrollOrResize) {
    window.removeEventListener('scroll', handleScrollOrResize)
    window.removeEventListener('resize', handleScrollOrResize)
  }
})
</script>

<style>
  @import '@/assets/css/variables.css';
/* instrument sans */
@import url('https://fonts.googleapis.com/css2?family=Instrument+Sans:wght@400;500;700;800&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Anton&display=swap');
@font-face {
  font-family: 'General-Sans';
  src: url('@/assets/fonts/GeneralSans-Variable.ttf') format('truetype'),
}
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: 'General-Sans', sans-serif;
  scroll-behavior: smooth;
}

html {
  --bottom-nav-height: 0px;
  --bottom-nav-offset: 0px;
  background-color: #0a0a0a;
}

/* ========================================
   SHELL: el body/app siempre ocupa al menos
   el viewport → el footer nunca "sube" y
   queda pegado al navbar mientras carga.
   ======================================== */
#app,
.app-shell {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  min-height: 100dvh;
}

.app-content {
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0;
}

/* La página activa estira su altura para empujar el footer abajo
   aunque el contenido aún esté cargando (solo altura, sin tocar display) */
.app-content > * {
  flex: 1 0 auto;
  width: 100%;
}

@media (max-width: 991.98px) {
  body {
    padding-bottom: var(--bottom-nav-height);
    background-color: #0a0a0a;
    color: var(--text-primary);
  }
}

@media (max-width: 991.98px) {
  body {
    padding-bottom: calc(var(--bottom-nav-height, 4.5rem) + var(--bottom-nav-offset, 0px));
  }
}

.container {
  max-width: var(--container-max, 1400px);
}

@media (max-width: 768px) {
  .container,
  .container-fluid {
    padding-left: 15px !important;
    padding-right: 15px !important;
  }
}

.bg-dark {
  background-color: var(--bg-dark, #000) !important;
}

.border-primary {
  border-color: var(--primary-color) !important;
}

.display-2, .display-3, .display-4, .display-5 {
  font-weight: 800;
}

.text-white {
  color: #ffffff !important;
}

.text-gradient-primary {
  background: linear-gradient(160deg, var(--primary-color), var(--primary-dark));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* settings-section (panel /configuracion) usa position:sticky; overflow:hidden lo rompe */
section:not(.settings-section) {
  position: relative;
  overflow: hidden;
}

.settings-section {
  position: relative;
  overflow: visible;
}

.h5 {
  font-size: 1.1rem;
  font-weight: 500;
  color: var(--text-primary);
}

.fs-6 {
  font-size: .8rem;
}

.fs-7 {
  font-size: 0.875rem !important;
}

.btn {
  font-size: .85rem !important;
  padding: 0.5rem 1rem;
  border-radius: var(--btn-radius);
  transition: all var(--transition-base);
}

.btn:hover {
  color: inherit;
}

.hover-opacity {
  transition: 0.3s ease;
}

.hover-opacity:hover {
  opacity: 0.8;
}

/* badges */
.status-badge {
  display: inline-flex;
  padding: 0.4rem 0.8rem;
  border-radius: 2rem;
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;
}

.btn-outline

.btn-outline-primary {
  background-color: transparent;
  color: var(--primary-color);
  border: 2px solid var(--primary-color);
  cursor: pointer;
  transition: all var(--transition-base);
}

.btn-outline-primary.active,
.btn-outline-primary:hover {
  background-color: var(--primary-color);
  color: #ffffff;
  border-color: var(--primary-color);
}

.text-primary {
  color: var(--primary-color) !important;
}

.bg-primary {
  background-color: var(--primary-color) !important;
}

.text-dark {
  color: var(--text-primary) !important;
}

.btn-primary {
  background-color: var(--btn-primary-bg) !important;
  color: var(--btn-primary-text) !important;
  border: none !important;
  padding: .5rem 1.3rem;
}

.btn-primary:hover {
  background-color: var(--btn-primary-hover) !important;
}

button, a {
  font-weight: 400;
  font-size: 1rem;
}

h1, h2, h3, h4, h5, h6 {
  font-weight: 700;
  color: var(--text-primary);
}

h1 { font-size: 2rem; }
h2 { font-size: 1.7rem; }
h3 { font-size: 1.5rem; font-weight: 600; letter-spacing: 0.2px; }
h4 { font-size: 1.25rem; font-weight: 600; }
h5 { font-size: 1rem; font-weight: 500; }

p {
  font-size: 1rem;
  color: var(--text-secondary);
  font-weight: 400;
  line-height: 1.6;
}

b { font-weight: 700; }

@media (max-width: 525px) {
  .display-2 { font-size: 3.5rem !important; }
}

@media (max-width: 400px) {
  .display-2 { font-size: 3rem !important; }
}

/* WhatsApp Floating Button Styles */
.whatsapp-float {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background-color: #25d366;
  color: #ffffff;
  border-radius: 50px;
  text-align: center;
  font-size: 30px;
  box-shadow: 0 4px 10px rgba(0,0,0,0.3);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  transition: all 0.3s;
}

.whatsapp-float:hover {
  background-color: #20ba5a;
  color: white;
}

.whatsapp-icon {
  margin-top: 4px;
}

.whatsapp-tooltip {
  position: absolute;
  right: 60px;
  background-color: #333;
  color: white;
  border-radius: 5px;
  padding: 8px 15px;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  visibility: hidden;
  opacity: 0;
  transition: all 0.3s;
  box-shadow: 0 2px 5px rgba(0,0,0,0.2);
}

.whatsapp-tooltip::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 100%;
  margin-top: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: transparent transparent transparent #333;
}

.whatsapp-float:hover .whatsapp-tooltip {
  visibility: visible;
  opacity: 1;
}

@media screen and (max-width: 768px) {
  .whatsapp-float {
    display: none;
  }
  .whatsapp-float i{
    margin-bottom: 5px;
  }
  
  .whatsapp-tooltip {
    display: none;
  }
}
</style>
